import PropTypes from 'prop-types';
import { set, sub } from 'date-fns';
import { useState } from 'react';
// @mui
import {
  Box,
  List,
  Badge,
  Button,
  Avatar,
  Tooltip,
  Divider,
  Popover,
  Typography,
  IconButton,
  ListItemText,
  ListItemAvatar,
  ListItemButton,
} from '@mui/material';
// utils
import { fToNow } from '../../../utils/formatTime';
// components
import Iconify from '../../../components/iconify';
import Scrollbar from '../../../components/scrollbar';

// ----------------------------------------------------------------------

// Bildirim öğesi için ayrı bir bileşen
function NotificationItem({ notification, onMarkAsRead }) {
  const { id, title, message, createdAt, isRead } = notification;

  // Göndericinin adının baş harflerini alarak veya belirli isimlere göre ikon belirleme
  const renderAvatarContent = (title) => {
    if (title.includes('Sistem')) {
      return <Iconify icon="eva:settings-2-fill" />;
    }
    if (title.includes('Destek')) {
      return <Iconify icon="eva:question-mark-circle-fill" />;
    }
    if (title.includes('Marketing')) {
      return <Iconify icon="eva:volume-up-fill" />;
    }
    if (title.includes('Finans')) {
      return <Iconify icon="eva:credit-card-fill" />;
    }
    const initials = title.split(' ').map(n => n[0]).join('').toUpperCase();
    return initials.length > 0 ? initials : <Iconify icon="eva:person-fill" />;
  };

  const handleMouseEnter = () => {
    if (!isRead) {
      onMarkAsRead(id);
    }
  };

  return (
    <ListItemButton
      sx={{
        py: 1.5,
        px: 2.5,
        mt: '1px',
        ...(!isRead && {
          bgcolor: 'action.selected',
        }),
      }}
      onMouseEnter={handleMouseEnter}
    >
      <ListItemAvatar>
        <Avatar sx={{ bgcolor: 'grey.400' }}>
          {renderAvatarContent(title)}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Typography variant="subtitle2">
            {title}
          </Typography>
        }
        secondary={
          <>
            <Typography component="span" variant="body2" sx={{ color: 'text.secondary', display: 'block' }}>
              {message}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                mt: 0.5,
                display: 'flex',
                alignItems: 'center',
                color: 'text.disabled',
              }}
            >
              <Iconify icon="eva:clock-outline" sx={{ mr: 0.5, width: 16, height: 16 }} />
              {fToNow(createdAt)}
            </Typography>
          </>
        }
      />
    </ListItemButton>
  );
}

NotificationItem.propTypes = {
  notification: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    message: PropTypes.string,
    createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
    isRead: PropTypes.bool,
  }).isRequired,
  onMarkAsRead: PropTypes.func.isRequired,
};

NotificationNewPopover.propTypes = {
  notifications: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  onMarkAsRead: PropTypes.func.isRequired,
  onMarkAllAsRead: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
};

export default function NotificationNewPopover({ notifications, loading, onMarkAsRead, onMarkAllAsRead, onRefresh }) {
  const [open, setOpen] = useState(null);

  const totalUnRead = notifications.filter((item) => !item.isRead).length;

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
    // onRefresh(); // Otomatik güncellemeyi kaldırdık
  };

  const handleClose = () => {
    setOpen(null);
  };

  return (
    <>
      <IconButton color={open ? 'primary' : 'default'} onClick={handleOpen} sx={{ width: 40, height: 40 }}>
        <Badge badgeContent={totalUnRead} color="error">
          <Iconify icon="eva:bell-fill" />
        </Badge>
      </IconButton>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 1.5,
            ml: 0.75,
            width: 360,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">Bildirimler</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {totalUnRead ? `${totalUnRead} okunmamış bildiriminiz var` : 'Okunmamış bildiriminiz yok'}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={onRefresh}
            sx={{ ml: 1 }}
          >
            Yenile
          </Button>
          {totalUnRead > 0 && (
            <Tooltip title="Tümünü okundu olarak işaretle">
              <IconButton color="primary" onClick={onMarkAllAsRead}>
                <Iconify icon="eva:done-all-fill" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Scrollbar sx={{ height: { xs: 340, sm: 'auto' }, maxHeight: 340 }}>
          {loading ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>Yükleniyor...</Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>Bildirim bulunmuyor</Box>
          ) : (
            <List disablePadding>
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={onMarkAsRead}
                />
              ))}
            </List>
          )}
        </Scrollbar>

        <Divider sx={{ borderStyle: 'dashed' }} />
      </Popover>
    </>
  );
}